"use client";
import PropertyCard, { type Property } from "~/components/PropertyCard";
import Img from "~/components/UI/Img";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getCampaigns } from "~/lib/api/campaign";
import {
  type IGetCampaignsInput,
  type IGetCampaignsResult,
} from "~/lib/api/campaign/types";
import { isErrorValue } from "~/lib/api/fetch/errors";
import { useMemo } from "react";
import { notAvailablePropertiesStatusMap } from "../../config";
import { Button } from "~/components/UI/Button";

export default function PropertiesSection(props: {
  initialQueryInput: IGetCampaignsInput;
  initialQueryData: IGetCampaignsResult["data"];
}) {
  const type =
    props.initialQueryInput.queryParams?.campaignStatus ?? "AVAILABLE";
  // const isAvailable = type === "AVAILABLE";

  const campaignsInfiniteQuery = useInfiniteQuery({
    queryKey: ["properties-campaigns", props.initialQueryInput],
    queryFn: (option) =>
      getCampaigns(option.pageParam).then((res) => {
        if (isErrorValue(res)) {
          throw res.error;
        }

        return {
          items: res.map((campaign) => ({
            id: campaign.campaignId,
            title: campaign.assetDetails.assetName,
            price: campaign.financialDetails.propertyPrice,
            priceCurrency: "AED",
            totalShares: campaign.financialDetails.noOfSharesRemaining,
            availableShares: campaign.financialDetails.noOfSharesRemaining,
            images: campaign.assetDetails.assetMediaFiles.map(
              (file) => file.filePath,
            ),
            annualizedReturnPercentage:
              campaign.financialDetails.projectedAnnualizedReturn,
            annualizedAppreciationPercentage:
              campaign.financialDetails.projectedAnnualAppreciation,
            projectedGrossYieldPercentage:
              campaign.financialDetails.projectedGrossYield,
            projectedNetYieldPercentage:
              campaign.financialDetails.projectedNetYield,
            isAvailable: true,
            minInvestmentPerShares: 100,
            minInvestmentPerSharesCurrency: "USD",
            sharesAmountForMinInvestment: 1,
          })) satisfies Property[],
          nextCursor: option.pageParam.queryParams.page + 1,
          hasNextPage: res.length >= option.pageParam.queryParams.limit,
        };
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage) {
        return;
      }

      return {
        ...props.initialQueryInput,
        queryParams: {
          ...props.initialQueryInput.queryParams,
          page: lastPage.nextCursor,
        },
      };
    },
    initialPageParam: props.initialQueryInput,
    // enabled: isAvailable,
  });

  const data = useMemo(
    () =>
      campaignsInfiniteQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [campaignsInfiniteQuery.data?.pages],
  );

  if (!data.length)
    return (
      <div className="flex flex-col gap-2 p-4 max-w-[440px] text-center items-center text-xs font-medium">
        <Img
          src="/images/svgs/icon-info.svg"
          alt="Info icon"
          className="size-4 text-accent"
          width={16}
          height={16}
        />
        <p>
          {type in notAvailablePropertiesStatusMap
            ? notAvailablePropertiesStatusMap[
                type as keyof typeof notAvailablePropertiesStatusMap
              ]
            : "No properties available"}
        </p>
      </div>
    );

  return (
    <section className="flex flex-col gap-12 px-8 max-w-[1500px]">
      <div className="flex flex-wrap justify-center gap-12 max-w-[1500px]">
        {data.map((property, index) => (
          <PropertyCard
            key={property.id + index}
            data={property}
            imgPriority={index < 2}
            // defaultExpanded={index === 1 ? "details" : "front"}
            // className={
            //   index === 1
            //     ? "sm:scale-120 scale-105 md:h-[38rem] md:w-[28rem]"
            //     : ""
            // }
          />
        ))}
      </div>

      <Button
        variant="secondary"
        size="lg"
        disabled={
          campaignsInfiniteQuery.isFetching ||
          !campaignsInfiniteQuery.hasNextPage
        }
        className="uppercase tracking-widest w-full max-w-[300px] mx-auto"
        onClick={() => campaignsInfiniteQuery.fetchNextPage()}
      >
        {campaignsInfiniteQuery.isFetching ? "Loading..." : "Load more"}
      </Button>
    </section>
  );
}
